import React, { useRef, useState } from 'react'
import injectSheet from 'react-jss'
import compose from 'recompose/compose'
import pure from 'recompose/pure'
import withStateHandlers from 'recompose/withStateHandlers'
import withHandlers from 'recompose/withHandlers'
import CloseButton from 'aria-viewer/tour/components/buttons/closeButton'
import ShareButton from 'aria-viewer/tour/components/buttons/shareButton'

import { MEDIA_SIZES } from 'aria-viewer/tour/constants'
import { AUDIO_SUPP } from 'aria-viewer/audiomanager/audioManagerActions'
import MediaComponent from 'aria-viewer/media/components/mediaComponent'
import StyleGuide from 'aria-viewer/tour/constants/styleGuide'
import { VideoPlayer } from 'aria-viewer/legacy'
import { sanitize } from 'dompurify'

const styleSheet = theme => ({
    header: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: '#121927',
        padding: 16,
    },

    body: {
        position: 'absolute',
        top: ({ media }) => ('instagram' == media.network ? '68px' : '0'),
        left: '0',
        width: '100%',
        height: '100%',
        zIndex: -1,
        transition: 'opacity .2s ease-in-out',
        opacity: ({ hasLoaded }) => (hasLoaded ? 1 : 1),
    },

    shareButton: {
        '& svg path': {
            fill: theme.palette.common.white,
        },

        '& div': {
            width: 18,
            height: 21,
        },
    },

    title: {
        color: theme.palette.common.white,
        fontSize: 32,
        lineHeight: '34px',
        textShadow: '0 4px 8px rgba(0, 0, 0, 0.7)',
        transition: 'opacity .2s ease-in-out',
        // pointerEvents: 'none',
        opacity: ({ showMediaTitle }) => (showMediaTitle ? 1 : 0),
    },

    readMore: {
        background: 'none',
        border: 'none',
        color: '#fff',
        fontSize: '16px',
        padding: 0,
        float: 'right',
        paddingTop: '8px',
        textShadow: '0 2px 4px rgba(0, 0, 0, 0.8)',
        transition: 'opacity .2s ease-in-out',
    },

    description: {
        color: theme.palette.common.white,
        fontSize: 16,
        textShadow: '0 2px 4px rgba(0, 0, 0, 0.8)',
        transition: 'opacity .2s ease-in-out',
        paddingTop: '8px',
        // pointerEvents: 'none',
        opacity: ({ showMediaTitle }) => (showMediaTitle ? 1 : 0),
        display: '-webkit-box',
        '-webkit-line-clamp': 2,
        '-webkit-box-orient': 'vertical',
    },

    background: {
        position: 'fixed',
        width: '100%',
        height: '100%',
        top: 0,
        left: 0,
        display: 'none',
        backgroundColor: '#1a1a1a',
    },

    closeReadMe: {
        top: '16px',
        right: '16px',
        position: 'absolute',
    },

    textContainer: {
        padding: '0 23px',
        position: 'absolute',
        bottom: '32px',
        zIndex: 1,
        overflow: 'auto',
    },

    image: {
        height: 375,
        width: '100%',
    },
})

const style = injectSheet(styleSheet)

const inlineStyle = {
    shareButton: {
        display: 'flex',
        width: 36,
        height: 36,
        alignItems: 'center',
        marginRight: 16,
    },
}

const state = withStateHandlers(({ hasLoaded = false, showMediaTitle = true }) => ({ hasLoaded, showMediaTitle }), {
    setHasLoaded: _ => loaded => ({ hasLoaded: loaded }),
    setShowMediaTitle: _ => show => ({ showMediaTitle: show }),
})

const handlers = withHandlers(({ setHasLoaded, setShowMediaTitle }) => ({
    onLoad: _ => e => setHasLoaded(true),
    onPlay: _ => e => setShowMediaTitle(false),
}))

const MediaFullScreen = ({
    classes,
    className,
    media,
    title,
    description,
    onClose,
    containerSize,
    onLoad,
    onPlay,
    ...props
}) => {
    // description ref //
    const descRef = useRef()

    // expanded state //
    const [expanded, setExpanded] = useState(false)

    // read more boolean //
    let showReadMore = true

    // check if description is more than 2 lines //
    if (descRef.current) {
        showReadMore = descRef.current.offsetHeight >= 48 ? true : false
    }

    // expanded styles //
    let overrideDescStyles = {
        overflow: showReadMore && !expanded ? 'hidden' : 'inherit',
        display: showReadMore && !expanded ? '-webkit-box' : 'block',
        opacity: expanded && 1,
    }
    let overrideTextContainer = {
        top: expanded ? '56px' : 'inherit',
    }
    let overrideTitleStyle = {
        opacity: expanded && 1,
    }

    return (
        <div className={className}>
            <div className={classes.body}>
                <MediaComponent
                    audioType={AUDIO_SUPP}
                    active={true}
                    enableClick={true}
                    enableInteractiveElements={false}
                    options={{ ...media, autoplay: false, size: MEDIA_SIZES.medium, disableBlur: true }}
                    aspect="fit"
                    showUI={media.type == VideoPlayer.types.AWS} /* only use system UI for AWS */
                    style={containerSize}
                    secondaryOptions={{
                        startCoord: {
                            lat: media.start_lat ? media.start_lat : 0,
                            lon: media.start_lon ? media.start_lon : 0,
                        },
                        delay: StyleGuide.delay.mainMediaTransition,
                    }}
                    key={media.type + '_' + media.id}
                    onLoad={onLoad}
                    onPlay={onPlay}
                />
            </div>

            <div className={classes.textContainer} style={overrideTextContainer}>
                <div className={classes.title} style={overrideTitleStyle}>
                    {title}
                </div>
                <div
                    ref={descRef}
                    className={classes.description}
                    style={overrideDescStyles}
                    dangerouslySetInnerHTML={{ __html: sanitize(description, { ADD_ATTR: ['target'] }) }}
                ></div>
                {showReadMore && !expanded && (
                    <button className={classes.readMore} onClick={() => setExpanded(true)}>
                        Read More
                    </button>
                )}
            </div>

            <div className={classes.background} style={{ display: expanded ? 'block' : 'none' }}>
                <div className={classes.closeReadMe}>
                    <CloseButton onClick={() => setExpanded(false)} />
                </div>
            </div>

            <div className={classes.header}>
                <CloseButton onClick={onClose} />

                <ShareButton style={inlineStyle.shareButton} className={classes.shareButton} />
            </div>
        </div>
    )
}

export default compose(state, handlers, style, pure)(MediaFullScreen)
